import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AddButton,
  ConfirmModal,
  CustomModal,
  FilterGroupItem,
  GenericTable,
  POSIcon,
  POSIconButtonWithTooltip,
  SortDirection,
  SortState,
} from '@publicisgroupe/p-os-components';
import useGetMetadataByEntityNameQuery from '../../hooks/queries/useGetMetadataByEntityNameQuery';
import { Entity } from '../../interfaces/Entity';
import { Field } from '../../interfaces/Field';
import { ViewMode, getFormattedValue } from '../../utils/valueFormatUtils';
import { ITEMS_PER_PAGE_DATA_TABLE } from '../../constants/DataTable';
import useDeleteEntityMutation from '../../hooks/mutation/useDeleteEntityMutation';
import { ViewModal } from '../ViewModal/ViewModal';
import { EditableModal } from '../EditableModal/EditableModal';
import { ModalTitle } from '../../enums/ModalTitle';

import './ConfigEntityGrid.less';
import useGetDocumentByIdQuery from '../../hooks/queries/useGetDocumentByIdQuery';
import { useQueryClient } from 'react-query';
import { DOCUMENT } from '../../constants/queryKeys';
import useGetEntityByFilterQuery from '../../hooks/queries/useGetEntityByFilterQuery';
import { FilterFieldDto, FilterFieldsDto } from '../../models';
import { FiltersConfiguration } from '../FiltersConfiguration/FiltersConfiguration';
import { XdmContextTableData } from '../XdmContextTableData/XdmContextTableData';
import { FieldsType } from '../../constants/FieldsType';
import { xdmContextDelimiter } from '@publicisgroupe/p-os-components/build/components/POSGroupFilter/POSGroupFilterConstants';

type Props = {
  entityName: string;
};

interface PageData {
  continuationToken: string | undefined;
  data: Entity[];
}

export const ConfigEntityGrid: React.FC<Props> = ({ entityName }) => {
  const itemsPerPage = ITEMS_PER_PAGE_DATA_TABLE;
  const [modalTitle, setModalTitle] = useState<string>(ModalTitle.add);
  const [skipToken, setSkipToken] = useState<string>();
  const [selectedEntityData, setSelectedEntityData] = useState<Entity>();
  const [isViewMode, setIsViewMode] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [dataPages, setDataPages] = useState<PageData[]>([]);
  const [entityData, setEntityData] = useState<Entity[]>([]);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const [sortState, setSortState] = useState<SortState<Entity, string>>({
    name: 'Created',
    selector: (item: Entity) => item.createdBy?.date.toString() ?? '',
    direction: SortDirection.Descending,
  });
  const [updatedDocumentId, setUpdatedDocumentId] = useState('');
  const [selectedFilterItems, setSelectedFilterItems] = useState<
    FilterGroupItem[]
  >([]);

  const queryClient = useQueryClient();
  const { data: updatedDocumentData, isFetching: fetchingUpdatedDocument } =
    useGetDocumentByIdQuery(entityName, updatedDocumentId, !!updatedDocumentId);

  const { data: metadata, isLoading: isLoadingMetadata } =
    useGetMetadataByEntityNameQuery(entityName);

  const xdmContextFieldsMapping = useMemo(
    () => metadata?.fields?.find((x) => x.type === 'xdmcontext'),
    [metadata]
  );

  const renderExpandableRowContent = (rowData: Entity[]) => (
    <XdmContextTableData data={rowData} mappings={xdmContextFieldsMapping} />
  );

  const filterData = useMemo<FilterFieldsDto>(() => {
    let fields: FilterFieldDto[] = [];
    if (selectedFilterItems && selectedFilterItems.length > 0) {
      const itemsGroupedByFieldName = selectedFilterItems.reduce(
        (acc, item) => {
          //item.groupId is a field name
          if (!acc[item.groupId]) {
            acc[item.groupId] = [];
          }
          acc[item.groupId].push(item.itemName);
          return acc;
        },
        {}
      );

      // Form response object
      fields = Object.entries(itemsGroupedByFieldName)
        .map(([fieldName, values]) => {
          let field: FilterFieldDto;
          const isXdmContext = fieldName.indexOf(xdmContextDelimiter) > -1;
          if (isXdmContext) {
            const xdmContextFieldName = fieldName.split(xdmContextDelimiter)[0];
            const subFieldName = fieldName.split(xdmContextDelimiter)[1];

            const subfield: FilterFieldDto = {
              name: subFieldName,
              values: values as string[],
              subFields: [],
            };
            field = {
              name: xdmContextFieldName,
              values: [],
              subFields: [subfield],
            };
          } else {
            field = {
              name: fieldName,
              values: values as string[],
              subFields: [],
            };
          }
          return field;
        })
        .reduce((acc, curr) => {
          const hasSubfields = curr.subFields.length > 0;
          const fieldWithTheSameNameAlreadyAdded = acc.find(
            (x) => x.name === curr.name
          );
          if (!hasSubfields || !fieldWithTheSameNameAlreadyAdded) {
            acc.push(curr);
          } else {
            fieldWithTheSameNameAlreadyAdded.subFields.push(...curr.subFields);
          }
          return acc;
        }, [] as FilterFieldDto[]);
    }
    return {
      entityName: entityName,
      fields: fields,
    };
  }, [entityName, selectedFilterItems]);

  const { data: paginatedResponse, isLoading: isLoadingEntityData } =
    useGetEntityByFilterQuery(
      entityName,
      filterData,
      itemsPerPage,
      skipToken,
      sortState
    );

  const { mutateAsync: deleteMutate } = useDeleteEntityMutation();

  const fields = useMemo(() => {
    if (!metadata || isLoadingMetadata) {
      return [];
    }
    return (
      metadata?.fields.filter(
        (field: Field) => !field.hideOnOverview && !field.ignore
      ) ?? []
    );
  }, [isLoadingMetadata, metadata]);

  const isFieldSortable = useCallback((field: Field) => {
    return !(field.isArray || field.xdm || field.xdmContext);
  }, []);

  const columns = useMemo<any>(() => {
    return [
      ...fields.map((field: Field) => {
        return {
          name: field.name,
          selector: field.name,
          sortable: isFieldSortable(field),
          cell: (item: Entity) => {
            const fieldKey =
              field.name.charAt(0).toLowerCase() + field.name.slice(1);
            const fieldValue = item[fieldKey] ?? '';
            return getFormattedValue(fieldValue, field, ViewMode.DataTable);
          },
        };
      }),
      {
        name: 'Created By',
        selector: 'Created By',
        sortable: true,
        cell: (item: Entity) => item.createdBy?.name ?? '',
      },
      {
        name: 'Created',
        selector: 'Created',
        sortable: true,
        cell: (item: Entity) =>
          item.createdBy?.date
            ? new Date(item.createdBy?.date).toLocaleString()
            : '',
      },
      {
        name: 'Modified By',
        selector: 'Modified By',
        sortable: true,
        cell: (item: Entity) => item.updatedBy?.name ?? '',
      },
      {
        name: 'Modified',
        selector: 'Modified',
        sortable: true,
        cell: (item: Entity) =>
          item.updatedBy?.date
            ? new Date(item.updatedBy?.date).toLocaleString()
            : '',
      },
    ];
  }, [fields, isFieldSortable]);

  const handleView = useCallback((clickedItem: Entity, event?: any) => {
    const isClickOnExpandIcon = event?.target.nodeName === 'I';
    if (isClickOnExpandIcon) {
      return;
    }

    setSelectedEntityData(clickedItem);
    setIsViewMode(true);
  }, []);

  const triggerOpenModal = useCallback((isAddMode = false) => {
    setIsAddMode(isAddMode);
    setIsOpen((prev) => !prev);
  }, []);

  const triggerConfirmModal = useCallback(
    () => setShowDeleteConfirmationModal((prev) => !prev),
    []
  );

  const handleAdd = useCallback(() => {
    setModalTitle(ModalTitle.add);
    setSelectedEntityData(undefined);
    triggerOpenModal(true);
  }, [triggerOpenModal]);

  const handleEdit = useCallback(
    (clickedItem: Entity) => {
      setModalTitle(ModalTitle.edit);
      setIsViewMode(false);
      setSelectedEntityData(clickedItem);
      triggerOpenModal();
    },
    [triggerOpenModal]
  );

  const handleClose = useCallback(() => {
    setSelectedEntityData(undefined);
    triggerOpenModal();
  }, [triggerOpenModal]);

  const handleCloseViewModal = useCallback(() => {
    setSelectedEntityData(undefined);
    setIsViewMode(false);
  }, []);

  const handleScroll = (event: any) => {
    const horizontalScrollBarHeight =
      event.target.offsetHeight - event.target.clientHeight;
    const element = event.target;
    const position =
      element.scrollHeight -
      Math.ceil(element.scrollTop) -
      horizontalScrollBarHeight;
    //if position smaller or equal than clientHeight that means we hit the bottom
    const isBottom = position <= element.clientHeight;
    if (isLoadingEntityData || !isBottom) return;
    paginatedResponse?.continuationToken &&
      setSkipToken(paginatedResponse.continuationToken);
  };

  useEffect(() => {
    if (isLoadingEntityData) {
      return;
    }

    if (!skipToken) {
      setDataPages([
        {
          continuationToken: skipToken,
          data: paginatedResponse?.data || [],
        },
      ]);

      return;
    }

    setDataPages((currentDataPages) => {
      if (!!currentDataPages?.find((p) => p.continuationToken === skipToken)) {
        return currentDataPages.map((p) => {
          if (p.continuationToken === skipToken) {
            return {
              continuationToken: skipToken,
              data: paginatedResponse?.data || [],
            };
          }

          return p;
        });
      }

      return [
        ...(currentDataPages || []),
        {
          continuationToken: skipToken,
          data: paginatedResponse?.data || [],
        },
      ];
    });
  }, [paginatedResponse, skipToken, isLoadingEntityData]);

  useEffect(() => {
    const entityDataAfterScroll = dataPages.reduce<Entity[]>((prev, curr) => {
      return [...prev, ...curr.data];
    }, []);
    setEntityData(entityDataAfterScroll);
  }, [dataPages]);

  useEffect(() => {
    if (updatedDocumentId && updatedDocumentData && !fetchingUpdatedDocument) {
      const currentTableData = [...entityData];
      const updatedData = currentTableData.map((doc) => {
        if (doc.id == updatedDocumentData.id) {
          return updatedDocumentData;
        }
        return doc;
      });
      setEntityData([...updatedData]);
      setUpdatedDocumentId('');
    }
  }, [
    entityData,
    fetchingUpdatedDocument,
    updatedDocumentData,
    updatedDocumentId,
  ]);

  const onDeleteButtonClick = useCallback(
    (item: Entity) => {
      selectedEntityData?.id !== item.id && setSelectedEntityData(item);
      triggerConfirmModal();
    },
    [selectedEntityData, triggerConfirmModal]
  );

  const handleDelete = useCallback(async () => {
    if (selectedEntityData) {
      await deleteMutate({ entityName, data: selectedEntityData });
      setSelectedEntityData(undefined);
      setShowDeleteConfirmationModal(false);

      setSkipToken(undefined);
    }
  }, [selectedEntityData, deleteMutate, entityName]);

  const handleResetPagination = useCallback(() => {
    setSkipToken(undefined);
  }, []);

  const handleSort = useCallback((selector: any) => {
    setSortState((prevState) => {
      const sortDirection =
        prevState.direction === SortDirection.Ascending
          ? SortDirection.Descending
          : SortDirection.Ascending;

      return {
        ...prevState,
        name: selector.name,
        selector: selector.selector,
        direction:
          selector.name !== prevState.name
            ? SortDirection.Ascending
            : sortDirection,
      };
    });
  }, []);

  const handleOnUpdate = useCallback((docId: string) => {
    setUpdatedDocumentId(docId);
  }, []);

  const handleFilterReset = useCallback(() => {
    setSelectedFilterItems([]);
    setSkipToken(undefined);
  }, []);

  const handleFilterButtonClick = useCallback(() => {
    queryClient.invalidateQueries([DOCUMENT, entityName]);
    setIsFilterModalOpen(true);
  }, [entityName, queryClient]);

  const handleFilterModalOnClose = useCallback(() => {
    setIsFilterModalOpen(false);
  }, []);

  const onSelectedFilterItemsChanged = useCallback(
    (filterItems: FilterGroupItem[]) => {
      setSelectedFilterItems(filterItems);
      setIsFilterModalOpen(false);
    },
    []
  );

  const keyName = useMemo(
    () =>
      metadata?.fields.find((el) => el.type === FieldsType.xdmContext)?.name,
    [metadata]
  );

  return (
    <>
      <div className="actions">
        <POSIconButtonWithTooltip
          iconOnly={false}
          labelPosition={'right'}
          className="filter-button"
          inverted={!selectedFilterItems.length}
          tooltip={'Filter'}
          icon={'filter'}
          floated="right"
          onClick={handleFilterButtonClick}
          tooltipSize="mini"
          type="button"
        >
          Filters ({selectedFilterItems.length})
        </POSIconButtonWithTooltip>
        {selectedFilterItems.length > 0 && (
          <div className={'close-link'} onClick={handleFilterReset}>
            <div>
              <p>Clear Filters</p>
            </div>
            <div className={'icon-close'}>
              <POSIcon icon={'close'} className="medium" />
            </div>
          </div>
        )}
        <AddButton onClick={handleAdd}>Add Data</AddButton>
      </div>
      <div className="data-table-scrollable" onScroll={handleScroll}>
        <GenericTable
          showArrowForRowWithSubItems
          showTableHeadForSubItems
          keyNameSubItemsProperty={keyName}
          expandableRowContent={(rowData) =>
            renderExpandableRowContent(rowData)
          }
          onRowClick={(event, data) => handleView(data, event)}
          getId={(x) => `${x.id}`}
          columnDefinitions={columns ?? []}
          className="p-os-grid"
          data={entityData ?? []}
          sortState={sortState}
          onSort={handleSort}
          hidePagination={true}
          pageCount={0}
          pageIndex={0}
          onPageChange={() => null}
          actions={{
            orientation: 'horizontal',
            actions: [],
            menu: {
              menuItems: [
                {
                  icon: 'view',
                  name: 'View',
                  onClick: handleView,
                },
                {
                  icon: 'edit',
                  name: 'Edit',
                  onClick: handleEdit,
                },
                {
                  icon: 'delete',
                  name: 'Delete',
                  onClick: onDeleteButtonClick,
                },
              ],
            },
          }}
        />
        {isLoadingEntityData && <div className="message">Loading...</div>}
      </div>
      <ConfirmModal
        openConfirmModal={showDeleteConfirmationModal}
        headerContent="You are about to delete data"
        onConfirm={handleDelete}
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        setOpenConfirmModal={triggerConfirmModal}
        subHeaderContent="Deleted data can not be restored later. Do you want to continue to remove the data?"
      />
      {metadata && isOpen && (
        <EditableModal
          modalTitle={modalTitle}
          entityData={selectedEntityData ?? {}}
          metadata={metadata}
          open={isOpen}
          isAddMode={isAddMode}
          onClose={handleClose}
          onUpdate={handleOnUpdate}
          resetPagination={handleResetPagination}
        />
      )}
      {selectedEntityData && metadata && isViewMode && (
        <ViewModal
          entityData={selectedEntityData}
          metadata={metadata}
          open={isViewMode}
          onClose={handleCloseViewModal}
          onDelete={onDeleteButtonClick}
          onEdit={handleEdit}
        />
      )}
      {isFilterModalOpen && metadata && (
        <CustomModal
          open={isFilterModalOpen}
          eventPool="modal"
          onClose={handleFilterModalOnClose}
          header={<h4>Filters</h4>}
          size="fullscreen"
        >
          <CustomModal.Content>
            <FiltersConfiguration
              metadata={metadata}
              entityName={entityName}
              selectedFilterItems={selectedFilterItems}
              onSelectedFilterItemsChanged={onSelectedFilterItemsChanged}
            />
          </CustomModal.Content>
        </CustomModal>
      )}
    </>
  );
};
